import { Injectable } from '@angular/core'
import { LocalStorage, Webstorable } from 'ngx-store'
import { Subject } from 'rxjs'
import { MediaFile, MediaRootDetail } from 'elements-sdk'

import { ParametersService } from 'main/services/parameters.service'
import { MediaConstants, MediaFileSort } from 'media/constants'

export type MediaLibraryViewStyle = 'gallery'|'list'|'compact'

interface ViewOptionsEntry {
  view?: MediaLibraryViewStyle
  sort?: string
  sortReverse?: boolean
}

@Injectable({ providedIn: 'root' })
export class MediaLibraryViewOptionsService {
  @LocalStorage('mlView') protected storage: Webstorable & Record<number, ViewOptionsEntry|undefined> = {} as any
  @LocalStorage('mlViewStyle') protected default: MediaLibraryViewStyle = 'gallery'
  @LocalStorage('mlViewSort') protected _defaultSort = 'name'
  @LocalStorage('mlViewSortReverse') protected _defaultSortReverse = false
  @LocalStorage('mlViewShowDeleted') protected _showDeleted = true
  @LocalStorage('mlViewHideUnrecognized') protected _hideUnrecognized = false

  change$ = new Subject<void>()

  constructor (
    parameters: ParametersService,
    private constants: MediaConstants,
  ) {
    if (parameters.values.mediaForceShowDeleted !== null && parameters.values.mediaForceShowDeleted !== undefined) {
      this.showDeleted = parameters.values.mediaForceShowDeleted
    }
  }

  getViewStyle (directory?: MediaFile, root?: MediaRootDetail): MediaLibraryViewStyle {
    if (directory && this.storage[directory.id]?.view) {
      return this.storage[directory.id]!.view!
    } else if (root && this.storage[-root.id]?.view) {
      return this.storage[-root.id]!.view!
    } else {
      return (root?.viewStyle as any) ?? this.default
    }
  }

  setViewStyle (directory: MediaFile|undefined, root: MediaRootDetail|undefined, style: MediaLibraryViewStyle): void {
    if (directory) {
      this.storage[directory.id] ??= {} as any
      this.storage[directory.id]!.view = style
    } else if (root) {
      this.storage[-root.id] ??= {} as any
      this.storage[-root.id]!.view = style
    } else {
      this.default = style
    }
    this.storage.save()
    this.change$.next()
  }

  get showDeleted (): boolean {
    return this._showDeleted
  }

  set showDeleted (value: boolean) {
    if (value !== this._showDeleted) {
      this._showDeleted = value
      this.change$.next()
    }
  }

  get hideUnrecognized (): boolean {
    return this._hideUnrecognized
  }

  set hideUnrecognized (value: boolean) {
    if (value !== this._hideUnrecognized) {
      this._hideUnrecognized = value
      this.change$.next()
    }
  }

  getSort (directory?: { id: number }): MediaFileSort {
    if (directory && this.storage[directory.id]?.sort) {
      return this.constants.mediaFileSorts.find(s =>
        s.id === this.storage[directory.id]!.sort &&
        s.reverse === this.storage[directory.id]!.sortReverse,
      ) ?? this.defaultSort
    } else {
      return this.defaultSort
    }
  }

  setSort (directory: { id: number }, sort: MediaFileSort): void {
    this.storage[directory.id] ??= {} as any
    this.storage[directory.id]!.sort = sort.id
    this.storage[directory.id]!.sortReverse = sort.reverse
    this.storage.save()
    this.change$.next()
  }

  get defaultSort (): MediaFileSort {
    return this.constants.mediaFileSorts.find(s =>
      s.id === this._defaultSort &&
      s.reverse === this._defaultSortReverse,
    ) ?? this.constants.mediaFileSorts[0]
  }

  set defaultSort (sort: MediaFileSort) {
    this._defaultSort = sort.id
    this._defaultSortReverse = sort.reverse
    this.change$.next()
  }
}
